import { useState } from 'react';
import { Camera, Check, ListTodo } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { uploadOpsPhoto, useOpsPhoto } from './photos';
import { isOverdue, useTaskMutations, useTasks, type Task } from './api';

/** המשימות שלי ושל הסניף (PRD 5.9.2). */
export function TasksPage() {
  const { me } = useMe();
  const tasks = useTasks();
  const { complete } = useTaskMutations();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (tasks.isPending) return <Skeleton className="m-4 h-40" />;
  if (tasks.isError) return <ErrorState onRetry={() => void tasks.refetch()} />;

  const all = tasks.data ?? [];
  const isOpen = (task: { completed_at: string | null; expired_at: string | null }) =>
    !task.completed_at && !task.expired_at;

  const mine = all.filter((task) => task.assignee_id === me?.employeeId && isOpen(task));
  const branch = all.filter(
    (task) => task.assignee_id !== me?.employeeId && isOpen(task),
  );
  const done = all.filter((task) => !isOpen(task));

  const finish = (task: Task, file?: File) => {
    setError(null);
    setBusy(task.id);

    const upload = file && task.branch_id
      ? uploadOpsPhoto(task.branch_id, 'task', file)
      : Promise.resolve(null);

    upload
      .then((photoPath) => complete.mutateAsync({ taskId: task.id, photoPath }))
      .catch((caught) => setError(errorMessage(caught)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title="משימות" subtitle="מה צריך לעשות, ועד מתי" />

      <div className="grid gap-6 px-4 pb-8">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {all.length === 0 && (
          <EmptyState title="אין משימות" description="כשתוטל עליך משימה היא תופיע כאן." />
        )}

        {mine.length > 0 && (
          <Section title="המשימות שלי" count={mine.length}>
            {mine.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                busy={busy === task.id}
                onFinish={(file) => finish(task, file)}
              />
            ))}
          </Section>
        )}

        {branch.length > 0 && (
          <Section title="משימות הסניף" count={branch.length}>
            {branch.map((task) => (
              <TaskCard key={task.id} task={task} busy={false} />
            ))}
          </Section>
        )}

        {done.length > 0 && (
          <Section title="הושלמו" count={done.length}>
            {done.slice(0, 20).map((task) => (
              <TaskCard key={task.id} task={task} busy={false} />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <h2 className="m-0 font-display text-display-m font-semibold">
        {title} <span className="text-body-sm font-normal text-muted">({count})</span>
      </h2>
      {children}
    </section>
  );
}

function TaskCard({
  task,
  busy,
  onFinish,
}: {
  task: Task;
  busy: boolean;
  onFinish?: (file?: File) => void;
}) {
  const photo = useOpsPhoto(task.completion_photo_path);
  const overdue = isOverdue(task);

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-title font-medium text-ink">
          <ListTodo size={20} strokeWidth={1.75} aria-hidden="true" />
          {task.title}
        </span>
        {task.completed_at ? (
          <StatusBadge tone="good">הושלמה</StatusBadge>
        ) : task.expired_at ? (
          // לא "הושלמה", וגם כבר לא משהו לעשות
          <StatusBadge tone="warn">לא בוצעה</StatusBadge>
        ) : overdue ? (
          <StatusBadge tone="bad">באיחור</StatusBadge>
        ) : (
          task.task_priorities && (
            <span className="text-label text-muted">{task.task_priorities.name}</span>
          )
        )}
      </div>

      {task.description && (
        <p className="m-0 whitespace-pre-line text-body-sm text-muted">{task.description}</p>
      )}

      <p className="m-0 text-label text-muted">
        {task.due_at && `עד ${formatDateTime(task.due_at)}`}
        {task.branches && ` · ${task.branches.name}`}
        {task.employees && ` · ${task.employees.first_name} ${task.employees.last_name}`}
      </p>

      {photo.data && (
        <img src={photo.data} alt="" className="max-h-48 w-full rounded-sm object-cover" />
      )}

      {onFinish && !task.completed_at && !task.expired_at && (
        <div className="flex flex-wrap gap-2">
          {task.requires_photo ? (
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-none border border-charcoal bg-charcoal px-6 font-display text-[20px] font-semibold text-on-charcoal">
              <Camera size={20} strokeWidth={1.75} aria-hidden="true" />
              {busy ? 'מעלה…' : 'לצלם ולסיים'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onFinish(file);
                }}
                className="sr-only"
              />
            </label>
          ) : (
            <Button variant="primary" disabled={busy} onClick={() => onFinish()}>
              <Check size={20} strokeWidth={1.75} aria-hidden="true" />
              סיימתי
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
